// src/modules/cmt-cia/mapaestagio.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, onSnapshot } from "firebase/firestore";
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { formatarData, getNormalizedDateString } from '../../utils/helpers';
import CadeteMap from '../../components/CadeteMap';

export const MapaEstagioCia = () => { 
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  
  const [todasEscolhas, setTodasEscolhas] = useState([]);
  const [allPelotoes, setAllPelotoes] = useState([]);
  const [allLocais, setAllLocais] = useState([]);
  
  const [selectedMonthTab, setSelectedMonthTab] = useState(''); 
  const [selectedDateTab, setSelectedDateTab] = useState(''); 
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    setLoading(true);
    const unsubEscolhas = onSnapshot(collection(db, paths.escolhas), (snapshot) => { 
        setTodasEscolhas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); 
    }, (err) => console.error("Erro (SOE Escolhas):", err));
    const unsubPelotoes = onSnapshot(collection(db, paths.pelotoes), (snapshot) => { 
        setAllPelotoes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); 
    }, (err) => console.error("Erro (SOE Pelotões):", err));
    const unsubLocais = onSnapshot(collection(db, paths.locais), (snapshot) => { 
        setAllLocais(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); 
    }, (err) => console.error("Erro (SOE Locais):", err));
    
    setLoading(false);
    return () => { unsubEscolhas(); unsubPelotoes(); unsubLocais(); };
  }, []);

  const pelotaoMap = useMemo(() => new Map(allPelotoes.map(p => [p.id, p])), [allPelotoes]);

  const diasAgrupados = useMemo(() => {
      const grupos = {};
      todasEscolhas.forEach(escolha => {
          if (escolha.opcao_impossibilidade || !escolha.data) return;
          const dataStr = getNormalizedDateString(escolha.data);
          if (!grupos[dataStr]) {
              grupos[dataStr] = {
                  dataStr: dataStr,
                  dataDisplay: formatarData(escolha.data),
                  data: escolha.data, 
                  itens: []
              };
          }
          grupos[dataStr].itens.push(escolha);
      });
      return Object.values(grupos).sort((a, b) => a.data.toDate() - b.data.toDate());
  }, [todasEscolhas]);

  const mesesAgrupados = useMemo(() => {
      const meses = {};
      diasAgrupados.forEach(dia => {
          const data = dia.data.toDate();
          const mesKey = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
          const mesNome = data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }); 
          if (!meses[mesKey]) {
              meses[mesKey] = { key: mesKey, nome: mesNome, dias: [] };
          }
          meses[mesKey].dias.push(dia);
      });
      return Object.values(meses).sort((a, b) => a.key.localeCompare(b.key));
  }, [diasAgrupados]);

  useEffect(() => {
      if (diasAgrupados.length > 0 && !selectedMonthTab) {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);

          let diaAlvo = diasAgrupados.find(d => {
              const dObj = d.data.toDate();
              return dObj.getDate() === hoje.getDate() && 
                     dObj.getMonth() === hoje.getMonth() && 
                     dObj.getFullYear() === hoje.getFullYear();
          });

          if (diaAlvo) {
              setSelectedDateTab(diaAlvo.dataStr);
              const dataObj = diaAlvo.data.toDate();
              const mesKey = `${dataObj.getFullYear()}-${String(dataObj.getMonth() + 1).padStart(2, '0')}`;
              setSelectedMonthTab(mesKey);
          } else {
              let diaProximo = diasAgrupados.find(d => d.data.toDate() >= hoje);
              if (!diaProximo) diaProximo = diasAgrupados[diasAgrupados.length - 1];

              if (diaProximo) {
                  const dataObj = diaProximo.data.toDate();
                  const mesKey = `${dataObj.getFullYear()}-${String(dataObj.getMonth() + 1).padStart(2, '0')}`;
                  setSelectedMonthTab(mesKey);
                  setSelectedDateTab('ALL'); 
              }
          }
      }
  }, [diasAgrupados, selectedMonthTab]);

  useEffect(() => {
      if (selectedMonthTab && mesesAgrupados.length > 0) {
          const mesAtual = mesesAgrupados.find(m => m.key === selectedMonthTab);
          if (mesAtual && mesAtual.dias.length > 0) {
              const diaAtualValido = selectedDateTab === 'ALL' || mesAtual.dias.some(d => d.dataStr === selectedDateTab);
              if (!diaAtualValido) {
                  setSelectedDateTab('ALL');
              }
          }
      }
  }, [selectedMonthTab, mesesAgrupados, selectedDateTab]);

  const verifySearchMatch = (item, term) => {
      if (!term) return true;
      const localInfo = allLocais.find(l => l.id === item.local_id);
      const localNome = localInfo ? localInfo.nome.toLowerCase() : '';
      const cadeteNome = (item.cadete_nome || '').toLowerCase();
      const busca = term.toLowerCase().trim();
      
      return cadeteNome.includes(busca) || localNome.includes(busca);
  };

  const itensFiltrados = useMemo(() => {
      if (!selectedMonthTab || !mesesAgrupados.length) return [];
      
      const mesAtual = mesesAgrupados.find(m => m.key === selectedMonthTab);
      if (!mesAtual) return [];

      let itensBrutos = [];
      if (selectedDateTab === 'ALL') {
          mesAtual.dias.forEach(d => {
              itensBrutos = itensBrutos.concat(d.itens);
          });
      } else {
          const diaAtual = mesAtual.dias.find(d => d.dataStr === selectedDateTab);
          if (diaAtual) itensBrutos = diaAtual.itens;
      }

      return itensBrutos.filter(item => verifySearchMatch(item, searchTerm));
  }, [selectedMonthTab, selectedDateTab, mesesAgrupados, searchTerm]);


  const mapData = useMemo(() => {
      const groupedByLocation = {};
      
      itensFiltrados.forEach(item => {
          const localId = item.local_id;
          const localInfo = allLocais.find(l => l.id === localId);
          
          if (localInfo && localInfo.latitude && localInfo.longitude) {
              if (!groupedByLocation[localId]) {
                  groupedByLocation[localId] = {
                      ...localInfo,
                      data_escolha: item.data,
                      tipo_escolha: item.tipo,
                      cadetes: [] 
                  };
              }
              const pelotaoNome = item.pelotao_origem || pelotaoMap.get(item.pelotao_id)?.nome || 'N/D';
              const dataFormatada = selectedDateTab === 'ALL' ? ` [${formatarData(item.data).substring(0,5)}]` : '';
              groupedByLocation[localId].cadetes.push(`${item.cadete_nome} (${pelotaoNome})${dataFormatada}`);
          }
      });
      
      return Object.values(groupedByLocation);
  }, [itensFiltrados, allLocais, pelotaoMap, selectedDateTab]);

  const resumoPelotoes = useMemo(() => {
      const contagem = {};

      itensFiltrados.forEach(item => {
          const nomePelotaoReal = item.pelotao_origem || pelotaoMap.get(item.pelotao_id)?.nome || 'Sem Pelotão';
          if (!contagem[nomePelotaoReal]) contagem[nomePelotaoReal] = 0;
          contagem[nomePelotaoReal]++;
      });

      return Object.entries(contagem)
          .map(([nome, total]) => ({ nome, total }))
          .sort((a, b) => a.nome.localeCompare(b.nome));

  }, [itensFiltrados, pelotaoMap]);

  // --- LÓGICA DE ZOOM E CENTRO INTELIGENTE ---
  const mapConfig = useMemo(() => {
    if (mapData.length === 0) return null;
    
    // Lista de prioridade para ancorar o mapa no centro
    const prioridades = ['copom', '13º bpm/m', '11º bpm/m', '7º bpm/m'];
    
    for (const prioridade of prioridades) {
        const baseEncontrada = mapData.find(loc => loc.nome.toLowerCase().includes(prioridade));
        if (baseEncontrada && baseEncontrada.latitude && baseEncontrada.longitude) {
            return { 
                center: [parseFloat(baseEncontrada.latitude), parseFloat(baseEncontrada.longitude)], 
                zoom: 10 // Valor menor = Mais afastado
            };
        }
    }
    
    // Se não tiver ninguém no Copom/13/11/7, afasta o zoom mesmo assim no 1º item que achar
    return {
        center: [parseFloat(mapData[0].latitude), parseFloat(mapData[0].longitude)],
        zoom: 10
    };
  }, [mapData]);

  if (loading) return <LoadingSpinner text="Carregando mapa e dados..." />;

  const mesSelecionadoObj = mesesAgrupados.find(m => m.key === selectedMonthTab);
  const totalEfetivo = resumoPelotoes.reduce((acc, curr) => acc + curr.total, 0);

  return (
    <div className="h-full flex flex-col">
      {/* HEADER RESTAURADO PARA O TAMANHO GRANDE PADRÃO */}
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Mapa de Estágios</h1>
        <p className="text-base text-gray-600">Visão Operacional - {userProfile?.nome || "Cmt"}</p>
      </header>

      <div className="animate-fade-in flex-1 flex flex-col">
          {diasAgrupados.length === 0 ? (
              <div className="p-8 text-center bg-gray-100 rounded-lg text-gray-500">
                  Não há estágios agendados no sistema.
              </div>
          ) : (
              <>
                  <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-3 flex flex-col gap-3">
                      
                      {/* Linha 1: Filtros e Busca */}
                      <div className="flex flex-col md:flex-row gap-3 items-center">
                          {/* Mês */}
                          <div className="w-full md:w-1/4 flex items-center gap-2">
                              <label className="text-xs font-bold text-gray-600 whitespace-nowrap">Mês:</label>
                              <select
                                  value={selectedMonthTab}
                                  onChange={(e) => setSelectedMonthTab(e.target.value)}
                                  className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded py-1.5 px-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer outline-none"
                              >
                                  {mesesAgrupados.map((mes) => (
                                      <option key={mes.key} value={mes.key}>{mes.nome.toUpperCase()}</option>
                                  ))}
                              </select>
                          </div>

                          {/* Dia */}
                          <div className="w-full md:w-1/4 flex items-center gap-2">
                              <label className="text-xs font-bold text-gray-600 whitespace-nowrap">Dia:</label>
                              <select
                                  value={selectedDateTab}
                                  onChange={(e) => setSelectedDateTab(e.target.value)}
                                  className="w-full bg-blue-50 border border-blue-300 text-blue-900 font-bold text-sm rounded py-1.5 px-2 focus:ring-blue-600 focus:border-blue-600 cursor-pointer outline-none"
                              >
                                  <option value="ALL">📋 Visão Geral do Mês</option>
                                  
                                  {mesSelecionadoObj && mesSelecionadoObj.dias.map((dia) => (
                                      <option key={dia.dataStr} value={dia.dataStr}>{dia.dataDisplay}</option>
                                  ))}
                              </select>
                          </div>

                          {/* Campo de Busca */}
                          <div className="w-full md:w-2/4 flex items-center gap-2 relative">
                              <label className="text-xs font-bold text-gray-600 whitespace-nowrap">Busca:</label>
                              <div className="relative w-full">
                                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                                  </div>
                                  <input 
                                      type="text" 
                                      placeholder="Nome do cadete ou local (ex: 20º BPM/M)"
                                      value={searchTerm}
                                      onChange={(e) => setSearchTerm(e.target.value)}
                                      className="w-full pl-8 pr-2 py-1.5 bg-white border border-gray-300 text-sm rounded focus:ring-blue-500 focus:border-blue-500 outline-none"
                                  />
                                  {searchTerm && (
                                      <button onClick={() => setSearchTerm('')} className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600">
                                          &times;
                                      </button>
                                  )}
                              </div>
                          </div>
                      </div>

                      {/* Linha 2: Resumo em Badges */}
                      {resumoPelotoes.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                              <span className="text-xs font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded border border-gray-200">
                                  Total: {totalEfetivo}
                              </span>
                              {resumoPelotoes.map((pel) => (
                                  <span key={pel.nome} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium">
                                      {pel.nome}
                                      <span className="bg-blue-600 text-white px-1.5 py-[1px] rounded-[4px] font-bold text-[10px]">
                                          {pel.total}
                                      </span>
                                  </span>
                              ))}
                          </div>
                      ) : (
                          searchTerm && (
                              <div className="pt-2 border-t border-gray-100 text-xs text-red-500 font-medium">
                                  Nenhum resultado encontrado para "{searchTerm}".
                              </div>
                          )
                      )}
                  </div>

                  {/* Componente do Mapa */}
                  <div className="bg-white p-2 md:p-4 rounded-xl shadow-lg border border-gray-200 flex-1 flex flex-col min-h-[500px]">
                      {mapData.length > 0 ? (
                          <div className="flex-1 w-full h-full rounded-lg overflow-hidden border border-gray-300">
                            {/* Passando as ordens de Zoom e Centro para o mapa */}
                            <CadeteMap 
                                locais={mapData} 
                                center={mapConfig?.center} 
                                zoom={mapConfig?.zoom} 
                            />
                          </div>
                      ) : (
                          <div className="p-8 flex-1 flex items-center justify-center text-center bg-gray-50 rounded-lg border border-gray-200 text-gray-500 text-sm">
                              Nenhum efetivo alocado em locais com coordenadas para os filtros atuais.
                          </div>
                      )}
                  </div>
              </>
          )}
      </div>
    </div>
  );
};